import { cn, focusRing } from "@simpro/ui";
import { Pause, Play } from "lucide-react";
import { useEffect, useState, useSyncExternalStore } from "react";
import { useMediaQuery } from "@/shared/hooks/useMediaQuery";

/** A picture as the landing page's content gives it. */
export interface HeroImage {
  url: string;
  altText: string;
}

/** How long each hero picture stays before the next fades in (FR-SA-206; design note salon-hero-carousel.md OQ 1). */
export const HERO_PICTURE_MS = 5_000;

/** What the arch's pictures and their controls share. */
export interface HeroCarousel {
  /** The picture shown, by position. */
  shown: number;
  count: number;
  /** Whether the pictures after the first may load: after the page's own load, never before its first paint. */
  rest: boolean;
  /** Whether it moves on by itself now. */
  running: boolean;
  /** Whether the customer stopped it with the pause button. */
  stopped: boolean;
  /** Under reduced motion it never moves by itself and has no pause button. */
  still: boolean;
  show: (index: number) => void;
  toggle: () => void;
  /** Spread on the arch and on the controls: pointer over either, or focus inside, holds the picture. */
  hold: {
    onPointerEnter: () => void;
    onPointerLeave: () => void;
    onFocus: () => void;
    onBlur: () => void;
  };
}

/**
 * The hero arch's pictures, one after another (TDD-SA FR-SA-206; design note salon-hero-carousel.md
 * section 5): every five seconds the next fades in, and after the last the first.
 * - It holds while the pointer is over the arch or its controls, while focus is inside them, and while the tab is hidden.
 * - The pause button stops it until it is pressed again (WCAG 2.2.2).
 * - Under reduced motion it never moves by itself (FR-SA-200).
 */
export function useHeroCarousel(count: number): HeroCarousel {
  const still = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [index, setIndex] = useState(0);
  const [stopped, setStopped] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const rest = useSyncExternalStore(
    (onChange) => {
      window.addEventListener("load", onChange);
      return () => window.removeEventListener("load", onChange);
    },
    () => document.readyState === "complete",
    () => false,
  );

  const running = count > 1 && rest && !still && !stopped && !hovered && !focused;

  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => {
      // A hidden tab keeps its picture; the customer comes back to the one they left.
      if (!document.hidden) setIndex((current) => (current + 1) % count);
    }, HERO_PICTURE_MS);
    return () => window.clearInterval(timer);
  }, [running, count]);

  return {
    shown: count > 0 ? index % count : 0,
    count,
    rest,
    running,
    stopped,
    still,
    show: setIndex,
    toggle: () => setStopped((current) => !current),
    hold: {
      onPointerEnter: () => setHovered(true),
      onPointerLeave: () => setHovered(false),
      onFocus: () => setFocused(true),
      onBlur: () => setFocused(false),
    },
  };
}

/**
 * The pictures, stacked in the arch: the shown one opaque, the others faded out and hidden from a
 * screen reader, which hears the shown picture's words. The first is the page's most important
 * picture and is fetched first; the rest wait for the page's own load.
 */
export function HeroSlides({ id, images, carousel }: { id: string; images: readonly HeroImage[]; carousel: HeroCarousel }) {
  return (
    <div
      id={id}
      role="group"
      aria-roledescription="carousel"
      aria-label="Salon pictures"
      // Announce the change only when the customer made it: a picture that moves by itself stays quiet.
      aria-live={carousel.running ? "off" : "polite"}
      className="absolute inset-0"
      {...carousel.hold}
    >
      {images.map((image, index) => {
        if (index > 0 && !carousel.rest) return null;
        const on = index === carousel.shown;
        return (
          <img
            key={`${image.url}-${index}`}
            src={image.url}
            alt={image.altText}
            aria-hidden={on ? undefined : true}
            fetchPriority={index === 0 ? "high" : "low"}
            decoding="async"
            className={cn(
              "animate-settle absolute inset-0 size-full object-cover transition-opacity duration-700 ease-out motion-reduce:transition-none",
              on ? "opacity-100" : "opacity-0",
            )}
          />
        );
      })}
    </div>
  );
}

/** The dots, one per picture, and the pause button, in a row under the arch. Never over a picture. */
export function HeroCarouselControls({ controls, carousel }: { controls: string; carousel: HeroCarousel }) {
  const { count, shown, stopped, still } = carousel;
  return (
    <div role="group" aria-label="Choose a salon picture" className="flex items-center gap-1 pt-3" {...carousel.hold}>
      {still ? null : (
        <button
          type="button"
          aria-controls={controls}
          aria-label={stopped ? "Play the pictures" : "Pause the pictures"}
          onClick={carousel.toggle}
          className={cn("mr-1 grid size-8 place-items-center rounded-full border border-edge text-heading hover:bg-muted-bg", focusRing)}
        >
          {stopped ? <Play className="size-3.5" aria-hidden /> : <Pause className="size-3.5" aria-hidden />}
        </button>
      )}
      {Array.from({ length: count }, (_, index) => (
        <button
          key={index}
          type="button"
          aria-controls={controls}
          aria-label={`Show picture ${index + 1} of ${count}`}
          aria-current={index === shown ? "true" : undefined}
          onClick={() => carousel.show(index)}
          className={cn("group grid size-6 place-items-center rounded-full", focusRing)}
        >
          <span
            aria-hidden="true"
            className={cn(
              "block h-2 rounded-full transition-all duration-300 motion-reduce:transition-none",
              index === shown ? "w-6 bg-heading" : "w-2 bg-edge group-hover:bg-subtle",
            )}
          />
        </button>
      ))}
    </div>
  );
}
